import React, { type ComponentProps } from "react";
import styled from "styled-components";

import Button from "@adsk/alloy-react-button";
import Divider from "@adsk/alloy-react-divider";
import FormField from "@adsk/alloy-react-form-field";
import { DocumentTwoIcon, TrashCanIcon } from "@adsk/alloy-react-icon";
import ProgressRing from "@adsk/alloy-react-progress-ring";
import Theme from "@adsk/alloy-react-theme";

import { UNSPECIFIED_STATUS_COLOR } from "../models";

export const ColumnFormField: React.ComponentType<
  ComponentProps<typeof FormField>
> = styled(FormField)`
  flex-direction: column;
`;

export const DeleteButton: typeof Button = styled(Button).attrs({
  alert: true,
  variant: Button.VARIANTS.TERTIARY,
  renderIcon: TrashCanIcon.renderIcon,
})`
  margin-bottom: 8px;
`;

export const DuplicateButton: typeof Button = styled(Button).attrs({
  alert: false,
  variant: Button.VARIANTS.TERTIARY,
  renderIcon: DocumentTwoIcon.renderIcon,
})`
  margin-bottom: 8px;
`;

export const ButtonDivider: React.ComponentType<
  ComponentProps<typeof Divider>
> = styled(Divider).attrs({
  variant: "vertical",
  height: "36px",
})`
  margin: 0 8px 8px 8px;
`;

export const ActionContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

export const ExpandedCell = styled.div<{ depth?: number }>`
  display: flex;
  align-items: center;
  margin-left: ${({ depth }) => (depth || 0) * 22}px;
`;

export const Form = styled.form`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: auto;
`;

export const FormFieldWithMargin: React.ComponentType<
  ComponentProps<typeof FormField>
> = styled(FormField).attrs({
  labelVariant: "top",
})`
  margin-top: 16px;

  &:first-child {
    margin-top: 0;
  }
`;

export const FormContainer = styled.div`
  padding: 8px 16px 16px 16px;
  overflow-y: auto;
  height: 100%;
`;

export const DatePickerFooter = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: flex-start;
  padding-left: 28px;
`;

export const NoWrap = styled.span(Theme.typography.ellipsis);

export const StatusLabel = styled.div<{ isUnspecified?: boolean }>`
  display: flex;
  align-items: center;

  &:before {
    content: " ";
    white-space: pre;
    display: inline-block;
    height: 16px;
    margin-right: 8px;
    border-radius: 4px;
    vertical-align: middle;
    background: ${({ color }) => color || UNSPECIFIED_STATUS_COLOR};
  }

  ${(props) =>
    props.isUnspecified &&
    `color: ${Theme.colors.charcoal500}; font-style: italic;`}
`;

export const FlexCenterDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

export const StickyHeaderTable = styled.table`
  height: 100%;
  border-spacing: 0; // needed to prevent sticky header from jumping
`;

export const StickyHeader = styled.thead`
  position: sticky;
  margin: 0;
  top: 0;
  z-index: 1;
`;

export const ProgressRingWithMargin = styled(ProgressRing)`
  margin-right: 10px;
`;
